import React, { Component, useEffect, useRef } from 'react'

import PropTypes from 'prop-types'
import Select, { components } from 'react-select'
import { v4 as uuid } from 'uuid'

import Message from 'components/message'

import { Container, InputLabel, StyledOption } from './styled'

const dropDownStyles = {
  multiValue: (base, state) => {
    return state.data.isFixed ? { ...base, backgroundColor: 'gray' } : base
  },
  multiValueLabel: (base, state) => {
    return state.data.isFixed
      ? { ...base, fontWeight: 'bold', color: 'white', paddingRight: 6 }
      : base
  },
  multiValueRemove: (base, state) => {
    return state.data.isFixed ? { ...base, display: 'none' } : base
  }
}

const CustomOption = ({ isSelected, children, isOptionDisabled }) => {
  const optionRef = useRef(null)

  useEffect(() => {
    if (optionRef && optionRef.current) {
      optionRef.current.parentNode.setAttribute('aria-selected', isSelected)
    }
  }, [isSelected, optionRef])

  return (
    <StyledOption ref={optionRef} isSelected={isSelected} isOptionDisabled={isOptionDisabled}>
      {children}
    </StyledOption>
  )
}

const SingleValue = ({ children, selectProps, ...props }) => {
  const isNumberOrString =
    typeof children.props.children === 'string' || typeof children.props.children === 'number'
  const optionLabel = isNumberOrString ? children.props.children : ''

  return (
    <components.SingleValue {...props}>
      {selectProps.customOptionLabel
        ? `${selectProps.customOptionLabel} ${optionLabel.toLowerCase()}`
        : optionLabel}
    </components.SingleValue>
  )
}

const IndicatorSeparator = ({ children, isClearable, hasValue, ...props }) =>
  isClearable && hasValue ? (
    <components.IndicatorSeparator {...props}>{children}</components.IndicatorSeparator>
  ) : null

class Dropdown extends Component {
  state = {
    selectedOption: this.props.value,
    isOpen: false
  }

  componentDidMount() {
    this.inputId = uuid()
  }

  handleChange = (selectedOption, { action, removedValue }) => {
    if (!this.props.disabled) {
      switch (action) {
        case 'clear':
          this.props.onChange({ selectedOption: { value: null, label: '' }, action, removedValue })
          break
        default:
          this.props.onChange({ selectedOption, action, removedValue })
          break
      }
    }
  }

  render() {
    return (
      <Container
        error={this.props.error}
        small={this.props.small}
        fontSize={this.props.fontSize}
        e2eClass={this.props.e2eClass}
        light={this.props.light}
        showBoxShadow={this.props.showBoxShadow}
      >
        {!this.props.noLabel && (
          <InputLabel htmlFor={this.props.inputId || this.inputId}>
            {this.props.placeholder}
          </InputLabel>
        )}
        <Select
          tabIndex={0}
          aria-label={this.props.ariaLabel}
          aria-expanded={this.state.isOpen}
          onMenuOpen={() => this.setState({ isOpen: true })}
          onMenuClose={() => this.setState({ isOpen: false })}
          small={this.props.small}
          filterOption={(option, inputValue) =>
            `${option.data.label}`.toLowerCase().includes(inputValue.toLowerCase())
          }
          components={{
            SingleValue,
            IndicatorSeparator: props => (
              <IndicatorSeparator isClearable={this.props.isClearable} {...props} />
            )
          }}
          customOptionLabel={this.props.customOptionLabel}
          getOptionLabel={option => (
            <CustomOption
              isSelected={this.props.value?.value == option.value}
              isOptionDisabled={option.disabledOption}
            >
              {option.label}
            </CustomOption>
          )}
          onBlur={this.props.onBlur}
          isMulti={this.props.isMulti}
          isSearchable={this.props.isSearchable}
          classNamePrefix="react-select"
          placeholder={this.props.placeholder}
          onChange={this.handleChange}
          options={this.props.options}
          isDisabled={this.props.disabled}
          isOptionDisabled={option => option.disabledOption}
          noOptionsMessage={() => this.props.noOptionsMessage}
          value={this.props.value}
          styles={dropDownStyles}
          isClearable={this.props.isClearable}
          openMenuOnFocus
          controlShouldRenderValue={this.props.controlShouldRenderValue}
          inputId={this.props.inputId || this.inputId}
          {...this.props.reactSelectProps}
        />
        {this.props.error && (
          <Message type="formInputError" e2eClass={this.props.e2eClass}>
            {this.props.error}
          </Message>
        )}
      </Container>
    )
  }
}

Dropdown.propTypes = {
  light: PropTypes.bool,
  showBoxShadow: PropTypes.bool
}

export default Dropdown
